// Feature 12: Causality Badge Component

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { CheckCircle, TrendingUp, ShowChart, Help } from '@mui/icons-material';
import { CausalityLevel } from '../../types/insightExplainability.types';

interface CausalityBadgeProps {
  level: CausalityLevel;
  confidence: number; // 0-1
  compact?: boolean;
}

const CausalityBadge: React.FC<CausalityBadgeProps> = ({ level, confidence, compact = false }) => {
  const getConfig = () => {
    switch (level) {
      case 'CAUSAL':
        return {
          icon: <CheckCircle fontSize="small" />,
          color: 'success' as const,
          label: compact ? 'Causal' : 'Strong Causality',
          tooltip: 'Strong evidence of causal relationship (A causes B)',
        };
      case 'PROBABLE':
        return {
          icon: <TrendingUp fontSize="small" />,
          color: 'info' as const,
          label: compact ? 'Probable' : 'Probable Causality',
          tooltip: 'Likely causal relationship with supporting evidence',
        };
      case 'CORRELATIONAL':
        return {
          icon: <ShowChart fontSize="small" />,
          color: 'warning' as const,
          label: compact ? 'Correlated' : 'Correlational',
          tooltip: 'Correlation observed, causality uncertain',
        };
      case 'WEAK':
        return {
          icon: <Help fontSize="small" />,
          color: 'default' as const,
          label: compact ? 'Weak' : 'Weak Evidence',
          tooltip: 'Limited evidence, treat as hypothesis',
        };
    }
  };

  const config = getConfig();
  const confidencePercent = Math.round(confidence * 100);

  return (
    <Tooltip
      title={
        <>
          {config.tooltip}
          <br />
          Confidence: {confidencePercent}%
        </>
      }
    >
      <Chip
        icon={config.icon}
        label={compact ? config.label : `${config.label} (${confidencePercent}%)`}
        color={config.color}
        size={compact ? 'small' : 'medium'}
        variant="outlined"
      />
    </Tooltip>
  );
};

export default CausalityBadge;
